import { Box, Loader, Text, TextButton } from '@wix/design-system';
import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { buildSwitchLinkStyle } from '../../utils/switchLinkStyle';
import React, { createContext, useContext, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';
import { useIsRtl } from '../../hooks';
import { dataHooks } from '../../dataHooks';
import { AuthMethod } from '../../utils/lastUsedAuthMethod';
import { ROUTES } from '../../routes';
import { LOGIN_FLOWS } from '../../stores/login';
import { EXPERIMENTS } from '../../stores/constants';
import { keys } from '../../translationsKeys';
import { AppContextProvider } from '../AppLoader';
import {
  AuthDivider,
  AuthPage,
  AuthPageContextProvider,
  BodyContainer,
} from '../AuthPage/AuthPage';
import authPageStyle from '../AuthPage/AuthPage.scss';
import { ButtonWithLoader } from '../ButtonWithLoader/ButtonWithLoader';
import { DialogFormContainer } from '../DialogContainer';
import { DialogHeader } from '../DialogHeader/DialogHeader';
import { InputFieldWithLabel } from '../InputField';
import { LoginSubTitle } from '../Login/Login';
import {
  PoliciesContainer,
  PoliciesVerticalContainer,
} from '../PoliciesContainer';
import { Recaptcha } from '../Recaptcha/Recaptcha';
import { SocialAuth, SocialAuthIconsWithApple } from '../SocialAuth/SocialAuth';
import { ThemedTextButton } from '../ThemedComponents';
import { FloatingNotification } from '../Common/FloatingNotification';
import s from './EmailStep.scss';

export interface EmailStepProps {
  showForgotEmailLink?: boolean;
  showContinueWithSsoLink?: boolean;
  policiesElement?: React.ReactNode;
}

interface EmailStepContext {
  store: AuthStore;
}

export const EmailStepContextProvider = createContext<EmailStepContext>(
  {} as any,
);

export const EmailStepHeader: React.FC = observer(({ children }) => {
  const { store } = useContext(EmailStepContextProvider);
  const { t } = useTranslation();

  return (
    <DialogHeader
      dataHook={dataHooks.emailStep.header}
      className={s.emailStepHeader}
    >
      <DialogHeader.Title
        flow="authPage"
        dataHook={dataHooks.emailStep.title}
        className={s.title}
      >
        {t(keys[store.titleKey])}
      </DialogHeader.Title>
      {children}
    </DialogHeader>
  );
});

const MobileEmailStepSocialLast: React.FC<EmailStepProps> = observer(
  ({ children }) => {
    const {
      rootStore: {
        displayStore: { sideBySideForm, isMobile },
        socialAuthStore,
      },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();
    return (
      <>
        {children}
        <Box
          gap={2}
          direction="horizontal"
          align="space-between"
          width={sideBySideForm.width}
          marginTop={3}
        >
          {!isMobile ||
            (!socialAuthStore.isUnsupportedAgentForSocialAuth && (
              <Box direction="vertical" verticalAlign="middle" marginBottom={3}>
                <Text>{t('emailStep.or.login.with')}</Text>
              </Box>
            ))}
          <SocialAuthIconsWithApple />
        </Box>
      </>
    );
  },
);

const EmailStepBody: React.FC<EmailStepProps> = observer(
  ({ showContinueWithSsoLink, children }) => {
    const {
      rootStore: {
        displayStore: {
          socialButtonsWidth,
          sideBySideForm,
          isMobile,
          switchLinkColor,
          switchLinkHoverColor,
          switchLinkFocusRingColor,
          switchLinkUnderline,
          switchLinkFontWeight,
        },
        ssoStore: { isSSOEnabledForFunnel },
        emailStepStore,
      },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();

    // Build inline style for switch link when preset has custom styling
    const switchLinkStyle = buildSwitchLinkStyle(
      switchLinkColor,
      switchLinkHoverColor,
      switchLinkFocusRingColor,
      switchLinkFontWeight,
    );

    return (
      <>
        {children}
        <AuthDivider
          className={classNames({ [authPageStyle.horizontal]: !isMobile })}
        />
        <Box
          gap={2}
          direction="vertical"
          align="right"
          width={sideBySideForm.width}
          verticalAlign="middle"
        >
          <SocialAuth />
          {showContinueWithSsoLink && isSSOEnabledForFunnel && (
            <Box width={socialButtonsWidth} align="center">
              <TextButton
                className={classNames(s.a11yFocus, {
                  [s.switchLink]: !!switchLinkColor,
                })}
                style={switchLinkStyle}
                onClick={emailStepStore.onClickContinueWithSso}
                size="small"
                underline={switchLinkUnderline ?? 'none'}
                dataHook={dataHooks.emailStep.continueToSsoLink}
              >
                {t(keys['emailStep.sso.button'])}
              </TextButton>
            </Box>
          )}
        </Box>
      </>
    );
  },
);

const EmailStepVerticalBody: React.FC<EmailStepProps> = observer(
  ({ showContinueWithSsoLink, children }) => {
    const {
      rootStore: {
        notificationStore,
        displayStore: {
          socialButtonsWidth,
          isMobile,
          authFormWidth,
          isDialogCentered,
          switchLinkColor,
          switchLinkHoverColor,
          switchLinkFocusRingColor,
          switchLinkUnderline,
          switchLinkFontWeight,
          verticalSocialFirst,
          preset,
        },
        emailStepStore: { loginEmailStepStore, onClickContinueWithSso },
        ssoStore: { isSSOEnabledForFunnel },
      },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();

    // Build inline style for switch link when preset has custom styling
    const switchLinkStyle = buildSwitchLinkStyle(
      switchLinkColor,
      switchLinkHoverColor,
      switchLinkFocusRingColor,
      switchLinkFontWeight,
    );

    // Get divider text from preset with fallback to default
    const dividerTextKey =
      preset.landingPage?.dividerTextKey ?? 'emailStep.divider';

    return (
      <AuthPageContextProvider.Provider value={{ store: loginEmailStepStore }}>
        <Box
          direction="vertical"
          width="100%"
          align="center"
          marginBottom={!isMobile && !isDialogCentered ? '40px' : undefined}
          dataHook={dataHooks.emailStep.container}
          className={isDialogCentered ? 'dialogCenteredWrapper' : undefined}
        >
          <AuthPage
            container={{
              dataHook: dataHooks.login.container,
              mainHeaderProps: {
                showBackButton: false,
              },
            }}
          >
            <BodyContainer className={s.vertical} withPolicies>
              <AuthPage.Header dataHooks={dataHooks.signup}>
                <LoginSubTitle flow={LOGIN_FLOWS.DEFAULT} />
              </AuthPage.Header>
              <AuthPage.VerticalBody
                socialFirst={verticalSocialFirst}
                dividerText={t(dividerTextKey)}
              >
                <Box marginBottom={2} width="100%" maxWidth={authFormWidth}>
                  {children}
                </Box>
              </AuthPage.VerticalBody>
              {!showContinueWithSsoLink && isSSOEnabledForFunnel && (
                <Box width={socialButtonsWidth} align="center">
                  <TextButton
                    className={classNames(s.a11yFocus, {
                      [s.switchLink]: !!switchLinkColor,
                    })}
                    style={switchLinkStyle}
                    onClick={onClickContinueWithSso}
                    size="small"
                    underline={switchLinkUnderline ?? 'none'}
                    dataHook={dataHooks.emailStep.continueToSsoLink}
                  >
                    {t(keys['emailStep.sso.button'])}
                  </TextButton>
                </Box>
              )}
              {notificationStore.isOpen && (
                <Box
                  width={socialButtonsWidth}
                  maxWidth="320px"
                  paddingTop="20px"
                  paddingBottom="24px"
                >
                  <FloatingNotification store={notificationStore} />
                </Box>
              )}
              <PoliciesVerticalContainer
                styleOverrides={{
                  noTopMargin: notificationStore.isOpen,
                }}
              >
                <PoliciesContainer.Minimal />
              </PoliciesVerticalContainer>
            </BodyContainer>
          </AuthPage>
          <AuthPage.Policies />
        </Box>
      </AuthPageContextProvider.Provider>
    );
  },
);

const MobileEmailStepSocialFirst: React.FC<EmailStepProps> = observer(
  ({ showContinueWithSsoLink, children }) => {
    const {
      rootStore: {
        displayStore: {
          socialButtonsWidth,
          sideBySideForm,
          isMobile,
          switchLinkColor,
          switchLinkHoverColor,
          switchLinkFocusRingColor,
          switchLinkUnderline,
          switchLinkFontWeight,
        },
        ssoStore: { isSSOEnabledForFunnel },
        emailStepStore,
      },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();

    // Build inline style for switch link when preset has custom styling
    const switchLinkStyle = buildSwitchLinkStyle(
      switchLinkColor,
      switchLinkHoverColor,
      switchLinkFocusRingColor,
      switchLinkFontWeight,
    );

    return (
      <>
        <Box
          gap={2}
          direction="vertical"
          align="right"
          width={sideBySideForm.width}
          verticalAlign="middle"
        >
          <SocialAuth />
          {showContinueWithSsoLink && isSSOEnabledForFunnel && (
            <Box width={socialButtonsWidth} align="center">
              <TextButton
                className={classNames(s.a11yFocus, {
                  [s.switchLink]: !!switchLinkColor,
                })}
                style={switchLinkStyle}
                onClick={emailStepStore.onClickContinueWithSso}
                size="small"
                underline={switchLinkUnderline ?? 'none'}
                dataHook={dataHooks.emailStep.continueToSsoLink}
              >
                {t(keys['emailStep.sso.button'])}
              </TextButton>
            </Box>
          )}
        </Box>
        <AuthDivider
          text={t(keys['login.divider.mobile'])}
          className={classNames({ [authPageStyle.horizontal]: !isMobile })}
        />
        {children}
      </>
    );
  },
);

const _EmailStep: React.FC<EmailStepProps> = observer(
  ({ showForgotEmailLink }) => {
    const {
      rootStore: {
        displayStore: {
          socialButtonsWidth,
          sideBySideForm,
          isMobile,
          isVerticalLayout,
          preset,
          isWixel,
          authFormWidth,
          inputButtonGap,
          inputForgotEmailGap,
          forgotEmailButtonGap,
        },
        emailStepStore,
      },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();
    const isRtl = useIsRtl();

    useEffect(() => {
      emailStepStore.emailField.clear();
    }, []);

    const inputBorder = isVerticalLayout ? 'standard' : 'bottomLine';
    const buttonSkin =
      preset.login?.primaryButtonDesign?.skin ??
      (emailStepStore.isEmailLoginMobileMode ? 'standard' : 'inverted');

    // Use inputForgotEmailGap when forgot email link is present, otherwise inputButtonGap
    const activeInputGap = showForgotEmailLink
      ? inputForgotEmailGap
      : inputButtonGap;

    // Only set CSS variables when explicitly configured in preset
    const customGapStyle: React.CSSProperties = {
      ...(activeInputGap && {
        ['--input-button-gap' as any]: activeInputGap,
        ['--input-with-label-padding-bottom' as any]: `calc(${activeInputGap} / 2)`,
      }),
      ...(forgotEmailButtonGap && {
        ['--forgot-email-button-gap' as any]: forgotEmailButtonGap,
      }),
    };

    // Gap for Box component (expects WDS units) - use 4 for legacy presets, 0 when CSS controls spacing via forgotEmailButtonGap
    const boxGapValue = forgotEmailButtonGap ? 0 : 4;

    // Determine forgot email link alignment
    const getForgotEmailAlignment = () => {
      const configuredAlignment = preset.formSpacing?.forgotEmailAlignment;
      if (configuredAlignment) {
        return configuredAlignment;
      }
      return !emailStepStore.isEmailLoginMobileMode ? 'left' : undefined;
    };

    return (
      <Box
        width={sideBySideForm.width}
        paddingBottom={0}
        align={isVerticalLayout ? 'center' : undefined}
      >
        <Box
          width={socialButtonsWidth}
          direction="vertical"
          paddingBottom={0}
          style={customGapStyle}
        >
          {isMobile &&
          !emailStepStore.isEmailLoginMobileMode &&
          !isVerticalLayout ? (
            <InputFieldWithLabel
              label={t(
                preset.landingPage?.emailLabelKey ??
                  keys['emailStep.email.label'],
              )}
              type="email"
              marginBottom={0}
              wrapperClassName={
                inputButtonGap ? s.emailInputCustomGap : s.emailInput
              }
              dataHook={dataHooks.emailStep.emailInput}
              lastUsedMethod={AuthMethod.EMAIL}
              border={inputBorder}
              onFocus={() => {
                emailStepStore.isEmailLoginMobileMode = true;
              }}
            />
          ) : (
            <>
              <InputFieldWithLabel
                required
                autoFocus
                {...(isWixel && {
                  className: classNames(s.inputField, s.wixel),
                })}
                formField={emailStepStore.emailField}
                ariaLabel={t(keys['emailStep.signup.or.login'])}
                label={t(
                  preset.landingPage?.emailLabelKey ??
                    keys['emailStep.email.label'],
                )}
                type="text"
                name="email"
                autocomplete="username webauthn"
                marginBottom={isMobile && !isVerticalLayout ? 2 : 0}
                wrapperClassName={
                  inputButtonGap ? s.emailInputCustomGap : s.emailInput
                }
                dataHook={dataHooks.emailStep.emailInput}
                lastUsedMethod={AuthMethod.EMAIL}
                border={inputBorder}
              />
              <Recaptcha />
              <Box
                className={classNames(
                  s.submitButtonWrapper,
                  preset.formSpacing?.forgotEmailButtonGap && s.useCssGap,
                )}
                direction="vertical"
                align={getForgotEmailAlignment()}
                gap={emailStepStore.isEmailLoginMobileMode ? 0 : boxGapValue}
              >
                {showForgotEmailLink && (isVerticalLayout || !isMobile) && (
                  <ThemedTextButton
                    className={s.a11yFocus}
                    onClick={emailStepStore.onClickForgotEmail}
                    size="small"
                    underline="always"
                    dataHook={dataHooks.emailStep.forgotEmailLink}
                    as="a"
                    href={emailStepStore.getAccountRecoveryLink('email')}
                  >
                    {t(keys['emailStep.forgotEmail.button'])}
                  </ThemedTextButton>
                )}
                <ButtonWithLoader
                  className={classNames(s.submitButton, s.a11yFocus, s.border)}
                  onClick={() => emailStepStore.submit()}
                  showLoader={emailStepStore.isLoading}
                  showArrowOnOver={
                    isVerticalLayout && preset.buttonDesign?.showArrowOnButton
                  }
                  dataHook={dataHooks.emailStep.submitButton}
                  type="submit"
                  fullWidth={
                    emailStepStore.isEmailLoginMobileMode ||
                    preset.login?.primaryButtonDesign?.fullWidth
                  }
                  skin={buttonSkin}
                  suffixIcon={
                    !preset.landingPage?.primaryButtonDesign?.hideSuffixIcon &&
                    !isVerticalLayout &&
                    !emailStepStore.isLoading ? (
                      isRtl ? (
                        <ChevronLeft />
                      ) : (
                        <ChevronRight />
                      )
                    ) : undefined
                  }
                >
                  {t(
                    preset.landingPage?.primaryButtonDesign?.textKey ??
                      keys['emailStep.submit'],
                  )}
                </ButtonWithLoader>
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  },
);

const getEmailStepBody = (
  showForgotEmailLink: boolean,
  showContinueWithSsoLink: boolean,
  isEmailLoginMobileMode: boolean,
  isMobile: boolean,
) => {
  if (isEmailLoginMobileMode) {
    return (
      <MobileEmailStepSocialLast>
        <_EmailStep showForgotEmailLink={false} />
      </MobileEmailStepSocialLast>
    );
  }
  if (isMobile) {
    return (
      <MobileEmailStepSocialFirst
        showContinueWithSsoLink={showContinueWithSsoLink}
      >
        <_EmailStep showForgotEmailLink={false} />
      </MobileEmailStepSocialFirst>
    );
  }

  return (
    <EmailStepBody showContinueWithSsoLink={showContinueWithSsoLink}>
      <_EmailStep showForgotEmailLink={showForgotEmailLink} />
    </EmailStepBody>
  );
};

const EmailStepWrapper: React.FC<EmailStepProps> = observer(
  ({
    children,
    policiesElement,
    showForgotEmailLink = false,
    showContinueWithSsoLink = false,
  }) => {
    const {
      rootStore: {
        displayStore: {
          isFullScreenMode,
          sideBySideForm,
          authFormWidth,
          isMobile,
          isDialogCentered,
        },
        emailStepStore: { isEmailLoginMobileMode },
        ssoStore: { isSSOEnabledForFunnel },
        notificationStore,
      },
    } = useContext(AppContextProvider);
    const shouldShoeContinueWithSsoLink =
      showContinueWithSsoLink && isSSOEnabledForFunnel;
    return (
      <Box
        direction="vertical"
        verticalAlign={isDialogCentered ? 'middle' : 'space-between'}
        align="center"
        height="100%"
        width="100%"
        marginBottom={!isMobile && !isDialogCentered ? '40px' : undefined}
        className={isDialogCentered ? 'dialogCenteredWrapper' : undefined}
      >
        <DialogFormContainer
          dataHook={dataHooks.emailStep.container}
          mainHeaderProps={{ showBackButton: false }}
        >
          {children}
          <Box
            direction={sideBySideForm.direction}
            maxWidth={authFormWidth}
            width="100%"
            height={sideBySideForm.height('small')}
          >
            {getEmailStepBody(
              showForgotEmailLink,
              shouldShoeContinueWithSsoLink,
              isEmailLoginMobileMode,
              isMobile,
            )}
          </Box>
        </DialogFormContainer>

        {notificationStore.isOpen && (
          <Box
            width={isMobile ? 'auto' : '100%'}
            align="center"
            marginTop={isMobile ? '11px' : '58px'}
            marginBottom={isMobile ? '11px' : '48px'}
            marginInlineStart={isMobile ? '12px' : undefined}
            marginInlineEnd={isMobile ? '12px' : undefined}
          >
            <FloatingNotification store={notificationStore} />
          </Box>
        )}

        <Box
          align="center"
          direction="vertical"
          width="100%"
          className={classNames(s.policiesContainer, {
            [s.modalPolicies]: !isFullScreenMode,
          })}
        >
          <PoliciesContainer
            styleOverrides={{
              noTopMargin: notificationStore.isOpen,
            }}
          >
            {policiesElement || <PoliciesContainer.Expanded />}
          </PoliciesContainer>
        </Box>
      </Box>
    );
  },
);

const EmailStep = Object.assign(EmailStepWrapper, {
  EmailStepHeader,
});

const LoginEmailStepDialog: React.FC = observer(() => {
  const {
    rootStore: {
      loginStore,
      emailStepStore: { loginEmailStepStore },
      displayStore: {
        isVerticalLayout,
        isWixel,
        switchLinkColor,
        switchLinkHoverColor,
        switchLinkFocusRingColor,
        switchLinkUnderline,
        switchLinkFontWeight,
      },
    },
  } = useContext(AppContextProvider);
  const { t } = useTranslation();

  // Build inline style for switch link when preset has custom styling
  const switchLinkStyle = buildSwitchLinkStyle(
    switchLinkColor,
    switchLinkHoverColor,
    switchLinkFocusRingColor,
    switchLinkFontWeight,
  );

  // Determine underline: preset value takes precedence, then layout-based default
  const underline =
    switchLinkUnderline ?? (isVerticalLayout ? 'always' : 'onHover');

  if (isVerticalLayout) {
    return (
      <EmailStepVerticalBody>
        <_EmailStep showForgotEmailLink={true} />
      </EmailStepVerticalBody>
    );
  }

  return (
    <EmailStepContextProvider.Provider value={{ store: loginEmailStepStore }}>
      <EmailStep
        showForgotEmailLink={true}
        showContinueWithSsoLink={true}
        policiesElement={<PoliciesContainer.Minimal />}
      >
        <EmailStep.EmailStepHeader>
          {loginEmailStepStore.subtitleKey && (
            <DialogHeader.SubTitleSmall>
              {t(keys[loginEmailStepStore.subtitleKey])}
            </DialogHeader.SubTitleSmall>
          )}
          {!loginStore.shouldBlockEditorXSignup() && (
            <DialogHeader.SubTitleSmall id={s.loginSubtitle}>
              <Trans i18nKey={keys['emailStep.login.subTitle']}>
                Dont have an account?
                <ThemedTextButton
                  className={classNames(s.link, {
                    [s.wixel]: isWixel,
                    [s.switchLink]: !!switchLinkColor,
                  })}
                  style={switchLinkStyle}
                  onClick={() => {
                    loginEmailStepStore.onHideLoginEmailStep();
                    loginStore.reportSwitchToSignup();
                  }}
                  underline={underline}
                  dataHook={dataHooks.loginEmailStep.switchToSignupBtn}
                  skin="standard"
                >
                  Sign Up
                </ThemedTextButton>
              </Trans>
            </DialogHeader.SubTitleSmall>
          )}
        </EmailStep.EmailStepHeader>
      </EmailStep>
    </EmailStepContextProvider.Provider>
  );
});

export const EmailStepDialog: React.FC = observer(() => {
  const location = useLocation();
  const {
    rootStore,
    rootStore: { emailStepStore, experiments, notificationStore },
  } = useContext(AppContextProvider);
  const { isFetchingByDefaultEmail, loginEmailStepStore } = emailStepStore;

  useEffect(() => {
    notificationStore.close();

    const isUsernamelessPasskeyEnabled =
      experiments.enabled(EXPERIMENTS.ENABLE_PASSKEY_LOGIN) &&
      experiments.enabled(EXPERIMENTS.ENABLE_USERNAMELESS_PASSKEY_LOGIN);
    if (isUsernamelessPasskeyEnabled) {
      emailStepStore.attemptUsernamelessWebAuthnLogin();
    }
    return () => {
      // Abort any pending usernameless credentials.get() when EmailStep unmounts
      // (i.e. user navigated away after logging in).
      rootStore.webauthnStore.abortWebAuthnAuthentication();
    };
  }, []);

  const customizedEmailStep = loginEmailStepStore.showLoginEmailStep ? (
    <LoginEmailStepDialog />
  ) : (
    <Redirect to={ROUTES.SIGNUP_PASSWORD + location.search} />
  );
  if (isFetchingByDefaultEmail) {
    return (
      <Box width="100%" height="100%" align="center" verticalAlign="middle">
        <Loader />
      </Box>
    );
  }
  return customizedEmailStep;
});
